import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { useQueryState, useQueryStates, parseAsString, createParser } from 'nuqs';
import { SortingState, ColumnFiltersState } from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ItemDataTable } from './components/ItemDataTable';
import { getColumns, editItemEvent, deleteItemEvent, manageItemPricesEvent } from './components/columns';
import { ItemForm } from './components/ItemForm';
import ItemPriceManager from './components/ItemPriceManager';
import {
  listItems,
  getItem,
  createItem,
  updateItem,
  deleteItem
} from '@/app/services/itemService';
import { Item, ItemCreate, ItemUpdate } from '@/app/types/item';
import { listCategories } from '@/app/services/categoryService';
import { listUnits } from '@/app/services/unitService';
import { listGodowns } from '@/app/services/inventoryService';
import { listTaxRates } from '@/app/services/taxRateService';
import { listAccounts } from '@/app/services/accountService';
import { getOrganizationSettings } from '@/app/services/organizationSettingsService';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse } from '@/app/types/api';

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'created_at', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'created_at',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'created_at-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

// Filter configuration for items
const FILTER_CONFIG = {
  format: 'array' as const,
  fields: {
    'name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'code': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'item_type': {
      op: 'eq'
    },
    'category_id': {
      op: 'eq',
      transform: (value: string): number => parseInt(value, 10)
    },
    'is_active': {
      op: 'eq',
      transform: (value: string): boolean => value === 'true'
    },
    'hsn_code': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'sale_price': {
      op: 'eq',
      transform: (value: string | number): number => {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
    }
  }
}

// Helper function to build filter parameters
const buildFilterParams = (
  filters: Record<string, string | number | boolean | { from?: string; to?: string }>
): string => {
  const filterArray: any[] = []
  
  Object.entries(filters).forEach(([field, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }
    
    // Handle date range filters
    if ((field === 'created_at' || field === 'updated_at') && typeof value === 'object' && 'from' in value) {
      const dateFilter = value as { from?: string; to?: string }
      if (dateFilter.from) {
        filterArray.push({
          field: field,
          value: `${dateFilter.from} 00:00:00`,
          op: 'gte'
        })
      }
      if (dateFilter.to) {
        filterArray.push({
          field: field,
          value: `${dateFilter.to} 23:59:59`,
          op: 'lte'
        })
      }
      return
    }
    
    // Get field configuration
    const fieldConfig = FILTER_CONFIG.fields[field as keyof typeof FILTER_CONFIG.fields]
    if (!fieldConfig) {
      // Default handling for unknown fields
      filterArray.push({
        field: field,
        value: String(value),
        op: 'eq'
      })
      return
    }
    
    // Transform value if needed
    const transformedValue = 'transform' in fieldConfig && fieldConfig.transform ? fieldConfig.transform(String(value)) : value
    
    filterArray.push({
      field: field,
      value: transformedValue,
      op: fieldConfig.op
    })
  })
  
  return JSON.stringify(filterArray)
}

const ItemsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([{ id: 'created_at', desc: true }]))
  
  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    name: parseAsString,
    code: parseAsString,
    item_type: parseAsString,
    category_id: parseAsString,
    is_active: parseAsString,
    hsn_code: parseAsString,
    created_at_from: parseAsString,
    created_at_to: parseAsString,
    sale_price: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })
  
  const [limit] = useState(10); // Keep limit state fixed
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  // New state for ItemPriceManager dialog
  const [isPriceManagerOpen, setIsPriceManagerOpen] = useState(false);
  const [selectedItemForPricing, setSelectedItemForPricing] = useState<Item | null>(null);
  
  // Build filters object from nuqs state
  const filters: Record<string, string | number | boolean | { from?: string; to?: string }> = {}
  
  // Add simple filters
  if (filterStates.name) filters.name = filterStates.name
  if (filterStates.code) filters.code = filterStates.code
  if (filterStates.item_type) filters.item_type = filterStates.item_type
  if (filterStates.category_id) filters.category_id = filterStates.category_id
  if (filterStates.is_active) filters.is_active = filterStates.is_active
  if (filterStates.hsn_code) filters.hsn_code = filterStates.hsn_code
  if (filterStates.sale_price) filters.sale_price = filterStates.sale_price
  
  // Add date range filters
  if (filterStates.created_at_from || filterStates.created_at_to) {
    filters.created_at = {
      from: filterStates.created_at_from || undefined,
      to: filterStates.created_at_to || undefined
    }
  }
  
  // Build column filters for DataTable compatibility
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const tableFilters: ColumnFiltersState = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        tableFilters.push({ id: key, value });
      }
    });
    return tableFilters;
  }, [filters]);

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0)
      return {
        sort_by: 'created_at',
        sort_dir: 'desc' as const
      }

    const sort = sorting[0]
    return {
      sort_by: sort.id,
      sort_dir: (sort.desc ? 'desc' : 'asc') as 'desc' | 'asc'
    }
  }, [sorting])

  // Fetch items with search, pagination, sorting and filters
  const { data: itemsData, isLoading, isError, error } = useQuery<ExtendedPaginatedResponse<Item>>({
    queryKey: ['items', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams()
      const skip = page * limit;
      
      // Build query parameters
      const params: any = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };
      
      // Add search if provided
      if (search) {
        params.search_query = search;
      }
      
      // Add filters if any
      if (Object.keys(filters).length > 0) {
        params.filters = buildFilterParams(filters);
      } else {
        params.filters = '[]'; // Send empty array to avoid backend warning
      }
      
      return listItems(params);
    },
    enabled: !!currentOrganizationId,
  });

  // Calculate page count from total items and limit if not directly provided by API
  const pageCount = itemsData?.page_count ?? itemsData?.pageCount ??
                   itemsData?.total_pages ?? itemsData?.totalPages ??
                   (itemsData ? Math.ceil(itemsData.total / limit) : 0);

  // Fetch dropdown data only when dialogs are open to reduce initial page load
  const shouldFetchDropdownData = isCreateDialogOpen || isEditDialogOpen;
  
  // Categories are always fetched since they're needed for the filter dropdown
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listCategories({ limit: 100, filters: '[]' }, currentOrganizationId);
      return result.items;
    },
    enabled: !!currentOrganizationId, // Always fetch categories
  });

  const { data: units = [], isLoading: unitsLoading } = useQuery({
    queryKey: ['units', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listUnits({ limit: 100, filters: '[]' }, currentOrganizationId);
      return result.items;
    },
    enabled: !!currentOrganizationId && shouldFetchDropdownData,
  });

  const { data: godowns = [], isLoading: godownsLoading } = useQuery({
    queryKey: ['godowns', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listGodowns({ limit: 100, filters: '[]' }); // Assuming godowns might not be org-specific? Adjust if needed
      return result.items;
    },
    enabled: !!currentOrganizationId && shouldFetchDropdownData,
  });

  const { data: taxRates = [], isLoading: taxRatesLoading } = useQuery({
    queryKey: ['taxRates', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listTaxRates({ limit: 100, filters: '[]' }, currentOrganizationId);
      return result.items;
    },
    enabled: !!currentOrganizationId && shouldFetchDropdownData,
  });

  const { data: accounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listAccounts({ limit: 200, filters: '[]' }, currentOrganizationId); // Increased limit for accounts
      return result.items;
    },
    enabled: !!currentOrganizationId && shouldFetchDropdownData,
  });

  // Fetch organization settings
  const { data: organizationSettings, isLoading: orgSettingsLoading } = useQuery({
    queryKey: ['organization-settings', currentOrganizationId],
    queryFn: () => getOrganizationSettings(),
    enabled: !!currentOrganizationId && shouldFetchDropdownData,
  });
  
  // Check if any dropdown data is still loading
  const dropdownDataLoading = categoriesLoading || unitsLoading || godownsLoading || taxRatesLoading || accountsLoading || orgSettingsLoading;

  // Mutations for CRUD operations (Unchanged)
  const createItemMutation = useMutation({
    mutationFn: (data: ItemCreate) => createItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] }); // Invalidate to refetch item list
      setIsCreateDialogOpen(false);
      toast.success('Item created successfully');
      // Trigger sync after item creation
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error: Error) => {
      toast.error(`Failed to create item: ${error.message}`);
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ItemUpdate }) => updateItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      toast.success('Item updated successfully');
      // Trigger sync after item update
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error: Error) => {
      console.error('Failed to update item:', error);
      toast.error(`Failed to update item: ${error.message}`);
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: number) => deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setIsDeleteAlertOpen(false);
      setSelectedItem(null);
      toast.success('Item deleted successfully');
      // Trigger sync after item deletion
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete item: ${error.message}`);
    },
  });

  // Event handlers for table actions (Unchanged logic, just setup)
  useEffect(() => {
    const handleEditItem = async (event: CustomEvent<{ item: Item }>): Promise<void> => {
      // Fetch full item details before opening edit dialog
      try {
        const fullItem = await getItem(event.detail.item.id);
        setSelectedItem(fullItem);
        setIsEditDialogOpen(true);
      } catch (error) {
        toast.error('Failed to load item details');
        console.error('Failed to fetch item details:', error);
      }
    };

    const handleDeleteItem = (event: CustomEvent<{ item: Item }>): void => {
      setSelectedItem(event.detail.item);
      setIsDeleteAlertOpen(true);
    };

    const handleManagePrices = (event: CustomEvent<{ item: Item }>): void => {
      setSelectedItemForPricing(event.detail.item);
      setIsPriceManagerOpen(true);
    };

    document.addEventListener(editItemEvent, handleEditItem as EventListener);
    document.addEventListener(deleteItemEvent, handleDeleteItem as EventListener);
    document.addEventListener(manageItemPricesEvent, handleManagePrices as EventListener);

    return () => {
      document.removeEventListener(editItemEvent, handleEditItem as EventListener);
      document.removeEventListener(deleteItemEvent, handleDeleteItem as EventListener);
      document.removeEventListener(manageItemPricesEvent, handleManagePrices as EventListener);
    };
  }, []); // Empty dependency array ensures this runs only once on mount/unmount

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0); // Reset to the first page on new search
  }, [setSearch, setPage]);

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0); // Reset to first page on sort change
  }, [setSorting, setPage]);

  // Handle page change from the DataTable
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, [setPage]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    // Build the new filter states for nuqs
    const updates: Record<string, string | null> = {}
    let hasActualChanges = false
    
    // List of all possible filter keys
    const knownFilterKeys = ['name', 'code', 'item_type', 'category_id', 'is_active', 'hsn_code', 'created_at', 'sale_price']
    
    // First, clear filters not in newFilters
    knownFilterKeys.forEach(key => {
      if (!(key in newFilters)) {
        if (key === 'created_at') {
          if (filterStates['created_at_from' as keyof typeof filterStates] !== null || filterStates['created_at_to' as keyof typeof filterStates] !== null) {
            hasActualChanges = true
            updates['created_at_from'] = null
            updates['created_at_to'] = null
          }
        } else if (filterStates[key as keyof typeof filterStates] !== null) {
          hasActualChanges = true
          updates[key] = null
        }
      }
    })
    
    // Then add the active filters
    Object.entries(newFilters).forEach(([key, value]) => {
      // Handle date range object
      if (typeof value === 'object' && value !== null && 'from' in value) {
        const dateRange = value as { from?: string; to?: string }
        const fromKey = `${key}_from` as keyof typeof filterStates
        const toKey = `${key}_to` as keyof typeof filterStates
        const newFrom = dateRange.from || null
        const newTo = dateRange.to || null
        
        if (filterStates[fromKey] !== newFrom || filterStates[toKey] !== newTo) {
          hasActualChanges = true
        }
        
        updates[`${key}_from`] = newFrom
        updates[`${key}_to`] = newTo
      } else if (value === undefined || value === null || value === '') {
        // Clear the filter
        if (filterStates[key as keyof typeof filterStates] !== null) {
          hasActualChanges = true
        }
        updates[key] = null
      } else {
        // Handle simple string/number values
        const newValue = String(value)
        if (filterStates[key as keyof typeof filterStates] !== newValue) {
          hasActualChanges = true
        }
        updates[key] = newValue
      }
    })
    
    // Update filters using nuqs
    setFilterStates(updates)
    
    // Reset to first page when filters change
    if (hasActualChanges) {
      setPage(0)
    }
  }, [filterStates, setFilterStates, setPage]);


  // Handle form submissions (Unchanged)
  const handleCreateItem = (data: ItemCreate): void => {
    createItemMutation.mutate(data);
  };

  const handleUpdateItem = (data: ItemUpdate): void => {
    if (selectedItem) {
      updateItemMutation.mutate({ id: selectedItem.id, data });
    }
  };

  // Handle delete confirmation (Unchanged)
  const handleDeleteItemConfirm = (): void => {
    if (selectedItem) {
      deleteItemMutation.mutate(selectedItem.id);
    }
  };
  
  // Create columns with categories (ensure categories is always an array)
  const columns = React.useMemo(() => {
    return getColumns(categories || []);
  }, [categories]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Items</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {isError && (
        <div className="flex items-center p-4 rounded-md bg-destructive/10 text-destructive border border-destructive">
          <AlertTriangle className="h-5 w-5 mr-3 flex-shrink-0" />
          <div>Error loading items: {(error as Error)?.message || 'An unknown error occurred'}</div>
        </div>
      )}

      {/* Items Data Table */}
      <ItemDataTable
        data={itemsData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        columnFilters={columnFilters}
        searchValue={search}
        onSearchChange={handleSearchChange}
      />

      {/* Create Item Dialog (Unchanged) */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>
              Create a new item (product or service) for your catalog.
            </DialogDescription>
          </DialogHeader>
          {dropdownDataLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading form data...</span>
            </div>
          ) : (
            <ItemForm
              onSubmit={handleCreateItem as (data: ItemCreate | ItemUpdate) => void}
              isSubmitting={createItemMutation.isPending}
              categories={categories}
              units={units}
              godowns={godowns}
              taxRates={taxRates}
              accounts={accounts}
              organizationSettings={organizationSettings}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setSelectedItem(null); // Clear selected item when dialog closes
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            <DialogDescription>
              Update the information for this item.
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            dropdownDataLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading form data...</span>
              </div>
            ) : (
              <ItemForm
                item={selectedItem}
                onSubmit={handleUpdateItem}
                isSubmitting={updateItemMutation.isPending}
                categories={categories}
                units={units}
                godowns={godowns}
                taxRates={taxRates}
                accounts={accounts}
                organizationSettings={organizationSettings}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Item Price Manager Dialog (Unchanged) */}
      {selectedItemForPricing && (
        <ItemPriceManager
          itemId={selectedItemForPricing.id}
          itemName={selectedItemForPricing.name}
          open={isPriceManagerOpen}
          onOpenChange={(open) => {
            setIsPriceManagerOpen(open);
            if (!open) setSelectedItemForPricing(null); // Clear selected item when closing
          }}
        />
      )}

      {/* Delete Confirmation Alert (Unchanged) */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the item &quot;{selectedItem?.name}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItemConfirm} // Use the confirmation handler
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteItemMutation.isPending} // Disable while deleting
            >
              {deleteItemMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ItemsPage;